import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appTrackerCopy from '@/i18n/ns/app-tracker';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useCompany } from '@/company/context';
import { retainedKey } from './retained-original';
import { InlineError } from '../../primitives';
import { limitsFromView, type AppBudgetApi } from '../budget-api';
import type { AppScheduleApi, PrivateSetup, ScheduleChoice } from '../schedule-api';
import type { AppSchedulePresentation } from '../control-selection-api';
import type { AppTrackerCopyKey } from '@/i18n/ns/app-tracker';
import { ScheduleForm } from './tracker-panels';
import { useAppBudget } from './use-app-budget';
import { useAppSchedule, type ScheduleNotice } from './use-app-schedule';
import { SchedulePicker } from './schedule-picker';
import { AppRuns } from './app-runs';
import type { AppRunsApi } from '../runs-api';
import { formatMoment, laterRunWords, laterRuns, scheduleWords } from './schedule-words';

/**
 * The production budget and schedule controls of ONE saved App, for the selected App pane to mount.
 *
 * Props contract (all ids are actual and exact, nothing is derived from a URL or a default):
 * - `artifactId` + `versionId`: the NAMED selected version of the saved App (never a logical id
 *   alone). The budget is read and saved for exactly that version; it works with no job at all.
 * - `logicalAppId` + `jobId`: the App's actual declared scheduled job. When `jobId` is absent no
 *   schedule section is shown. Its enable/pause/resume are real actions of this job only.
 * - `budgetApi` / `scheduleApi`: optional explicit clients (tests); the hooks bind the session
 *   guarded fetch themselves otherwise.
 * Nothing here writes on mount or refresh: reads happen, writes only follow an explicit press.
 */
export interface AppControlsPanelProps {
  artifactId: string;
  versionId: string;
  logicalAppId: string;
  jobId?: string | null;
  /**
   * The App's declared scheduled jobs in manifest order, each with its optional declared label. Several
   * jobs list one row each; a missing label shows no title for a single job and "Scheduled run {n}"
   * (its 1-based position) for several. An id or name is never shown. Replaces `jobId` when given.
   */
  jobs?: readonly {
    jobId: string;
    label?: string | null;
    schedule?: AppSchedulePresentation | null;
  }[];
  /** The selected version's revision (from the control selection): with `artifactId` and `versionId`, the exact
   * tuple the owner's first enable of a private App names. */
  revisionId?: string;
  budgetApi?: AppBudgetApi;
  scheduleApi?: AppScheduleApi;
  /** Tests: the runs client and where a Fix in chat draft goes. */
  runsApi?: AppRunsApi;
  onFixInChat?: (prompt: string) => Promise<void>;
  /** Tests: one explicit client per job (called once per row). */
  scheduleApiFor?: (jobId: string) => AppScheduleApi;
}

export function AppControlsPanel(props: AppControlsPanelProps) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const company = useCompany();
  // The coherent signed-in person and company (membership belongs to that company); without them nothing here can
  // be started, because a retained original has no one to belong to.
  const member = company.data?.membership;
  const companyId = company.data?.company?.id;
  const person =
    company.ready && member?.userId && companyId && member.organizationId === companyId
      ? { userId: member.userId, companyId }
      : null;
  const jobs = props.jobs ?? (props.jobId ? [{ jobId: props.jobId, label: null }] : []);
  return (
    <div className="pc-app-controls" data-testid="app-controls">
      <BudgetSection
        artifactId={props.artifactId}
        versionId={props.versionId}
        api={props.budgetApi}
      />
      {jobs.length > 0 && (
        <section className="pc-app-schedule" data-testid="app-schedule-controls">
          <h3>{copy.scheduleOnlyHeading}</h3>
          {jobs.map((job, index) => (
            <ScheduleRow
              // Another person, company or selected version is another row with its own retained original.
              key={`${job.jobId}\0${person?.userId}\0${person?.companyId}\0${props.versionId}\0${props.revisionId}`}
              person={person}
              appId={props.logicalAppId}
              jobId={job.jobId}
              declared={job.schedule ?? null}
              setup={
                props.revisionId
                  ? {
                      artifactId: props.artifactId,
                      versionId: props.versionId,
                      revisionId: props.revisionId,
                    }
                  : undefined
              }
              title={
                job.label
                  ? job.label
                  : jobs.length > 1
                    ? formatCopy(copy.scheduleJobOrdinal, { n: String(index + 1) })
                    : null
              }
              api={props.scheduleApi}
              apiFor={props.scheduleApiFor}
            />
          ))}
          <p className="pc-tracker-note">{copy.scheduleTimeNote}</p>
        </section>
      )}
      <AppRuns
        artifactId={props.artifactId}
        versionId={props.versionId}
        revisionId={props.revisionId}
        api={props.runsApi}
        onFixInChat={props.onFixInChat}
      />
    </div>
  );
}

function BudgetSection(props: { artifactId: string; versionId: string; api?: AppBudgetApi }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const budget = useAppBudget(props.artifactId, props.versionId, props.api);
  const limits = limitsFromView(budget.view);
  const view = budget.view;
  // The form restarts from the saved policy when it changes, EXCEPT while an attempt is unresolved:
  // the person's own amounts must stay exactly as sent, so a repeat is the original command even if
  // the policy moved in the background (the server then answers it, and only then do the fields
  // reload from what is saved).
  const [formKey, setFormKey] = useState(view?.policyVersion ?? 'unset');
  const policyKey = view?.policyVersion ?? 'unset';
  if (budget.notice !== 'unknown' && formKey !== policyKey) setFormKey(policyKey);
  const notice =
    budget.notice === 'not_authorized'
      ? copy.budgetDenied
      : budget.notice === 'changed'
        ? copy.budgetChanged
        : budget.notice === 'unknown'
          ? copy.budgetUnknown
          : budget.notice === 'invalid'
            ? copy.budgetInvalid
            : '';
  return (
    <section className="pc-app-budget" data-testid="app-budget">
      <h3>{copy.budgetHeading}</h3>
      {!view || budget.ownerOnly ? (
        // Unread is never an empty or default budget.
        budget.ownerOnly ? (
          <p role="status">{copy.budgetDenied}</p>
        ) : budget.unavailable ? (
          <InlineError message={copy.budgetUnavailable} onRetry={() => void budget.refresh()} />
        ) : (
          <p role="status" />
        )
      ) : (
        <>
          {view.state === 'unset' && <p className="pc-tracker-note">{copy.budgetNone}</p>}
          <ScheduleForm
            // A new policy (after a save or a refresh) starts the fields from what is saved now.
            key={formKey}
            budgetOnly
            initial={{
              time: '00:00',
              timezone: 'UTC',
              ...(limits ?? { perRunLimit: '', monthlyLimit: '' }),
            }}
            limits={view.limits}
            timezones={[]}
            saving={budget.busy}
            saved={limits ? { time: '00:00', timezone: 'UTC', ...limits } : null}
            onSave={(draft) => void budget.save(draft.perRunLimit, draft.monthlyLimit)}
          />
          <p role="status">{notice}</p>
        </>
      )}
    </section>
  );
}

function ScheduleRow(props: {
  appId: string;
  jobId: string;
  title: string | null;
  declared?: AppSchedulePresentation | null;
  setup?: PrivateSetup;
  person: { userId: string; companyId: string } | null;
  api?: AppScheduleApi;
  apiFor?: (jobId: string) => AppScheduleApi;
}) {
  const [forJob] = useState(() => props.apiFor?.(props.jobId));
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const [picking, setPicking] = useState(false);
  // The retained original for this person, company, selected version and job (see retained-original).
  const retainKey =
    props.setup && props.person
      ? retainedKey('schedule', props.person, [
          props.setup.artifactId,
          props.setup.versionId,
          props.setup.revisionId,
          props.jobId,
        ])
      : undefined;
  const schedule = useAppSchedule(
    props.appId,
    props.jobId,
    forJob ?? props.api,
    props.setup,
    retainKey,
  );
  const state = schedule.state;
  const leading =
    schedule.pendingAction ??
    (state?.state === 'active' ? 'pause' : state?.state === 'paused' ? 'resume' : 'enable');
  const notice = noticeText(schedule.notice, copy);
  // A refusal of a chosen cadence belongs beside the choice while the picker is open.
  const pickerError =
    picking && ['too_frequent', 'invalid', 'busy'].includes(schedule.notice ?? '') ? notice : null;
  const words = (presented: AppSchedulePresentation) =>
    `${scheduleWords(presented, copy, locale)} (${presented.timezone})`;
  const firstEnable =
    !state && schedule.notActivated && schedule.canEnablePrivate && props.declared && retainKey;
  const canChange = Boolean(
    retainKey &&
    (firstEnable || (state && state.state !== 'not_scheduled' && schedule.canReschedule)),
  );
  const choose = async (choice: ScheduleChoice) => {
    const outcome = firstEnable ? await schedule.enable(choice) : await schedule.reschedule(choice);
    if (outcome === 'ok') setPicking(false);
  };
  const shown = state?.schedule ?? (firstEnable ? (props.declared ?? null) : null);
  const next = state ? formatMoment(state.nextFireAt, locale, state.schedule?.timezone) : null;
  const later = laterRunWords(
    laterRuns(shown, state?.nextFireAt ?? null),
    state?.nextFireAt ?? shown?.nextRuns?.[0] ?? null,
    locale,
    shown?.timezone,
  );
  const replay = () =>
    // An unresolved original is replayed as itself, so its own action leads; a read of the
    // current state never swaps it for another.
    schedule.pendingAction
      ? schedule.retry()
      : leading === 'pause' || leading === 'resume' || leading === 'enable'
        ? schedule[leading]()
        : schedule.retry();
  return (
    <div className="pc-app-schedule-job" data-testid="schedule-job">
      {props.title && <h4 data-testid="schedule-job-title">{props.title}</h4>}
      {firstEnable && props.declared ? (
        // A private App that was never turned on: its declared cadence, and the owner's explicit enable.
        <>
          <p className="pc-tracker-summary" data-testid="schedule-facts">
            {`${copy.scheduleNotScheduled} · ${words(props.declared)}`}
          </p>
          <ScheduleDetails schedule={props.declared} later={later} />
          <div className="pc-tracker-actions">
            <Button
              type="button"
              variant="default"
              size="sm"
              data-testid="schedule-action"
              disabled={schedule.busy}
              onClick={() => void schedule.enable()}
            >
              {copy.scheduleEnable}
            </Button>
            {!picking && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="schedule-change"
                disabled={schedule.busy}
                onClick={() => setPicking(true)}
              >
                {copy.scheduleChoose}
              </Button>
            )}
          </div>
        </>
      ) : !state ? (
        <p role="status">
          {schedule.loadFailed || schedule.notActivated ? copy.scheduleUnavailable : ''}
        </p>
      ) : (
        <>
          <p className="pc-tracker-summary" data-testid="schedule-facts">
            {state.state === 'not_scheduled'
              ? copy.scheduleNotScheduled
              : [
                  state.state === 'paused' ? copy.paused : copy.scheduleOn,
                  // Only what the server presented; an unpresented cron stays absent.
                  state.schedule ? words(state.schedule) : null,
                  next ? formatCopy(copy.scheduleNext, { next }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
          </p>
          {state.schedule && <ScheduleDetails schedule={state.schedule} later={later} />}
          {retainKey && (
            <div className="pc-tracker-actions">
              <Button
                type="button"
                // Pausing is a quiet choice; turning on or resuming is the row's one primary action.
                variant={leading === 'pause' ? 'outline' : 'default'}
                size="sm"
                data-testid="schedule-action"
                disabled={schedule.busy || schedule.notice === 'storage'}
                onClick={() => void replay()}
              >
                {leading === 'pause'
                  ? copy.pause
                  : leading === 'resume'
                    ? copy.resume
                    : leading === 'reschedule'
                      ? copy.scheduleChange
                      : copy.scheduleEnable}
              </Button>
              {canChange && !picking && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="schedule-change"
                  disabled={schedule.busy || schedule.notice === 'storage'}
                  onClick={() => setPicking(true)}
                >
                  {copy.scheduleChange}
                </Button>
              )}
            </div>
          )}
        </>
      )}
      {picking && canChange && (
        <SchedulePicker
          initial={shown}
          saving={schedule.busy}
          error={pickerError}
          onSave={(choice) => void choose(choice)}
          onCancel={() => setPicking(false)}
        />
      )}
      <p role="status">{pickerError ? '' : notice}</p>
    </div>
  );
}

/** A custom schedule's own cron, and the runs after the next one, both from the server. */
function ScheduleDetails(props: { schedule: AppSchedulePresentation; later: string[] }) {
  const copy = useNamespaceCopy(appTrackerCopy);
  const locale = useAppCatalogLocale();
  const runs = (() => {
    try {
      return new Intl.ListFormat(locale, { type: 'conjunction' }).format(props.later);
    } catch {
      return props.later.join(', ');
    }
  })();
  return (
    <>
      {props.schedule.cadence === 'custom' && (
        <code className="pc-schedule-cron" data-testid="schedule-cron">
          {props.schedule.cron}
        </code>
      )}
      {props.later.length > 0 && (
        <p className="pc-tracker-note" data-testid="schedule-upcoming">
          {formatCopy(copy.scheduleThen, { runs })}
        </p>
      )}
    </>
  );
}

function noticeText(notice: ScheduleNotice, copy: Record<AppTrackerCopyKey, string>) {
  switch (notice) {
    case 'not_authorized':
      return copy.scheduleDenied;
    case 'conflict':
      return copy.scheduleConflict;
    case 'unknown':
      return copy.scheduleUnknown;
    case 'storage':
      return copy.scheduleUnavailable;
    case 'too_frequent':
      return copy.scheduleTooFrequent;
    case 'invalid':
      return copy.scheduleInvalidChoice;
    case 'busy':
      return copy.scheduleBusy;
    default:
      return '';
  }
}
